import type { BufferGeometry } from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { Vec2, Vec3, Ring } from '../../geo/types';
import { createMeshBuilder, type MeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { PATTERN } from '../../render/patterns/patternIds';
import { SURFACE } from '../mesh/attributes';
import { poissonInPolygon } from '../../geo/poisson';
import { outwardPolygon, outwardQuad, rectangle, regularPolygon, walls } from './solidParts';
import { buildDikeHouse, DIKE_HOUSE } from './dikeHouse';

const SMALL_FARM_LENGTH = 14;
const SMALL_FARM_WIDTH = 8;
const SMALL_FARM_HEIGHT = 4;
const LARGE_FARM_LENGTH = 24;
const LARGE_FARM_WIDTH = 12;
const LARGE_FARM_HEIGHT = 5;
const ROOF_PITCH = (50 * Math.PI) / 180;
const FARM_TREE_RADIUS = 25;
/* An inscribed polygon, so every sample of the disc lies within FARM_TREE_RADIUS of the farm. */
const FARM_TREE_DISC_SIDES = 48;
const FARM_TREE_POISSON_RADIUS = 6;
const FARM_TREE_MIN = 3;
const FARM_TREE_MAX = 5;

export type FarmSize = 'small' | 'large';
export type FarmForm = 'amstel-dike-house';

export interface FarmData {
  readonly id: string;
  readonly position: Vec2;
  readonly heading: number;
  readonly size: FarmSize;
  readonly form?: FarmForm;
}

export interface FarmPlacement {
  readonly id: string;
  readonly position: Vec2;
  readonly heading: number;
  readonly size: FarmSize;
  readonly form?: FarmForm;
}

function getFarmDimensions(
  size: FarmSize,
  form?: FarmForm
): {
  readonly length: number;
  readonly width: number;
  readonly height: number;
} {
  if (form === 'amstel-dike-house') return DIKE_HOUSE;
  if (size === 'small') {
    return { length: SMALL_FARM_LENGTH, width: SMALL_FARM_WIDTH, height: SMALL_FARM_HEIGHT };
  }
  return { length: LARGE_FARM_LENGTH, width: LARGE_FARM_WIDTH, height: LARGE_FARM_HEIGHT };
}

/* PLAN §5: a 50° hip roof, so every slope, the two hips included, rises at the same pitch. */
function addHipRoof(
  builder: MeshBuilder,
  style: VertexStyle,
  dims: { readonly length: number; readonly width: number; readonly height: number }
): void {
  const halfL = dims.length / 2;
  const halfW = dims.width / 2;
  const eaves = dims.height;
  const ridgeY = eaves + halfW * Math.tan(ROOF_PITCH);
  const ridgeX = halfL - halfW;
  const inside: Vec3 = [0, eaves, 0];
  for (const z of [-halfW, halfW]) {
    outwardQuad(
      builder,
      style,
      [
        [-halfL, eaves, z],
        [halfL, eaves, z],
        [ridgeX, ridgeY, 0],
        [-ridgeX, ridgeY, 0],
      ],
      inside
    );
  }
  for (const x of [-halfL, halfL]) {
    const ridgeEnd = Math.sign(x) * ridgeX;
    outwardPolygon(
      builder,
      style,
      [
        [x, eaves, -halfW],
        [x, eaves, halfW],
        [ridgeEnd, ridgeY, 0],
      ],
      inside
    );
  }
}

export function buildFarmTemplate(
  size: FarmSize,
  wallColor: LinearRGB,
  roofColor: LinearRGB,
  edgeId: number,
  seed: number,
  form?: FarmForm
): BufferGeometry {
  if (form === 'amstel-dike-house') return buildDikeHouse(wallColor, roofColor, edgeId, seed);
  const builder = createMeshBuilder();
  const dims = getFarmDimensions(size);
  const wallStyle = (facadeWidth: number): VertexStyle => ({
    color: wallColor,
    surface: SURFACE.wood,
    pattern: PATTERN.timberFront1538,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [facadeWidth, dims.height],
  });
  const roofStyle: VertexStyle = {
    color: roofColor,
    surface: SURFACE.roofThatch,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [dims.length, dims.width / 2 / Math.cos(ROOF_PITCH)],
  };
  walls(builder, wallStyle, rectangle(dims.length / 2, dims.width / 2), 0, dims.height);
  addHipRoof(builder, roofStyle, dims);
  return builder.build();
}

export function buildFarmObstacle(
  position: Vec2,
  heading: number,
  size: FarmSize,
  form?: FarmForm
): Ring {
  const { length, width } = getFarmDimensions(size, form);
  const halfL = length / 2;
  const halfW = width / 2;

  const cos = Math.cos(heading);
  const sin = Math.sin(heading);

  const corners: [number, number][] = [
    [-halfL, -halfW],
    [halfL, -halfW],
    [halfL, halfW],
    [-halfL, halfW],
  ];

  return corners.map(([x, y]): Vec2 => [
    position[0] + x * cos - y * sin,
    position[1] + x * sin + y * cos,
  ]);
}

export function getFarmTreeArea(position: Vec2): {
  readonly center: Vec2;
  readonly radius: number;
} {
  return {
    center: position,
    radius: FARM_TREE_RADIUS,
  };
}

/*
 * PLAN §5: 3-5 trees by Poisson 6 m within 25 m of the farm. isBlocked rejects a spot: water,
 * and in the feature also the farm house and every other building a crown would reach into.
 */
export function placeFarmTrees(
  position: Vec2,
  rng: Rng,
  isBlocked: (p: Vec2) => boolean
): readonly Vec2[] {
  const targetCount = rng.int(FARM_TREE_MIN, FARM_TREE_MAX);
  const disc: Ring = regularPolygon(FARM_TREE_RADIUS, FARM_TREE_DISC_SIDES).map((p): Vec2 => [
    position[0] + p[0],
    position[1] + p[1],
  ]);
  const candidates = poissonInPolygon({ outer: disc, holes: [] }, FARM_TREE_POISSON_RADIUS, rng);
  /* Farms stand by the dozen, so the blocking queries stop at the trees a farm keeps. */
  const kept: Vec2[] = [];
  for (const p of candidates) {
    if (kept.length === targetCount) break;
    if (!isBlocked(p)) kept.push(p);
  }
  return kept;
}

export { SMALL_FARM_LENGTH, SMALL_FARM_WIDTH, LARGE_FARM_LENGTH, LARGE_FARM_WIDTH };
